import * as React from "react";
import {Provider} from "react-redux";
import AdminUserAddPage from "../admin/AdminUserAddPage";
import AdminMassAssignUsersPage from "../admin/AdminMassAssignUsersPage";
import {AdminUserEditPage} from "../admin/AdminUserEditPage";
import MainAdminComponent from "../admin/MainAdmin.component";
import {ErrorComponent} from "../base/Error.component";
import LayoutFooter from "../base/LayoutFooter";
import LayoutHeaderAndBody from "../base/LayoutHeaderAndBody";
import ScrollToTop from "../base/ScrollToTop.component";
import DisclaimerPageConnection from "../disclaimer/DisclaimerPage";
import {FeedbackPage} from "../feedback/FeedbackPage.component";
import FeedbackSuccessPageConnection from "../feedback/FeedbackSuccessPage.component";
import FlashReportPage from "../flash-report/FlashReportPage";
import InitializeIntlComponent from "../initialize/InitializeIntl.component";
import {default as InitializeUserSession} from "../initialize/InitializeUserSession.component";
import ManagerPageConnection from "../manager/ManagerPage.component";
import MasterLectureSeriesPage from "../master-lecture-series/MasterLectureSeriesPage";
import MeetingNotePage from "../meeting-note/MeetingNotePage";
import MyClientsPage from "../my-clients/MyClientsPage";
import ReleaseNotesPage from "../release-notes/ReleaseNotesPage";
import AdvancedEditFileComponent from "../shared-documents/AdvancedEditFileComponent";
import {DownloadDocumentComponent} from "../shared-documents/DownloadDocumentComponent";
import WhitePapersPage from "../thought-leadership/WhitePapersPage";
import UploadPage from "../upload-page/UploadPage";
import RootPage from "./RootPage";
import AdminUploadPage from "../admin/AdminUploadPage";
import SharedDocumentsPage from "../shared-documents/SharedDocumentsPage";
import MarketOutlookPage from "../market-outlooks/MarketOutlook.page";
import PowerBiReportingPage from "../power-bi-reporting/PowerBiReportingPage";
import OddReportsPage from "../odd-reports/OddReportsPage";
import LatestMarketOutlookPage from "../market-outlooks/LatestMarketOutlook.page";
import {LatestPowerBiFaq} from "../power-bi-reporting/LatestPowerBiFaq";
import OddProductPage from "../odd-iq/odd-product-page/OddProductPage";
import OddIqPageContainer from "../odd-iq/OddIqPageContainer";
import {PowerBiReportAdminPage} from "../power-bi-admin/PowerBiReportAdminPage";
import AdminClientPlansPage from "../admin/AdminClientPlansPage";
import AdminEditClientPage from "../admin/AdminEditClientPage";
import AdminEditPlanPage from "../admin/AdminEditPlanPage";
import AdminMediaGroupsPage from "../cpr-media/AdminMediaGroupsPage";
import PlanSpecificMediaFilesPage from "../cpr-media/PlanSpecificMediaFilesPage";
import CprSharedMediaFilesAdminPage from "../cpr-media/SharedMediaFilesPage";
import {LoginCallback, Security, useOktaAuth} from "@okta/okta-react";
import {AuthRequiredModal} from "./AuthRequiredModal";
import {CorsErrorModal} from "./CorsErrorModal";
import {ICorsErrorModalOpen} from "../base/OktaLogout";
import {TriggerOktaSignInPage} from "./TriggerOktaSignInPage";
import {triggerOktaLogin} from "../../utils/oktaUtil";
import {getOktaAuth} from "../../utils/oktaAuthCreator";
import ProductPage from "../product/ProductPage";
import PortfolioPage from "../portfolio/PortfolioPage";
import ResearchPage from "../research/ResearchPage";
import Home from "../home-page/HomePage";
import {ModelPortfolioPage} from "../portfolio/ModelPortfolioPage";
import {Route, Routes, useNavigate} from "react-router-dom";
import {RequiredAuth} from "./SecureRoute";
// eslint-disable-next-line import/no-named-as-default
import OktaAuth from "@okta/okta-auth-js";
import {AppStore} from "../../../configureStore";

export interface IRootComponentProps {
    store: AppStore;
}

export const RouterSwitcherNoAuth = () => {
    return <Routes>
        <Route path="/login/callback" Component={LoginCallback}/>
        <Route path="/*" Component={RequiredAuth}>
            <Route path="" Component={TriggerOktaSignInPage}/>
        </Route>
    </Routes>;
};

export const RouteSwitcher = () => {
    return <Routes>
        <Route path="/" Component={RootPage}/>
        <Route path="/home" Component={Home}/>
        <Route path="/admin" Component={MainAdminComponent}/>
        <Route path="/admin/add-user" Component={AdminUserAddPage}/>
        <Route path="/admin/mass-assign-users" Component={AdminMassAssignUsersPage}/>
        <Route path="/admin/user/:arbtSecurityIdParam" Component={AdminUserEditPage}/>
        <Route path="/admin-client-plan" Component={AdminClientPlansPage}/>
        <Route path="/admin/client/:clientIdParam" Component={AdminEditClientPage}/>
        <Route path="/admin/plan/:planIdParam" Component={AdminEditPlanPage}/>
        <Route path="/admin-media-groups" Component={AdminMediaGroupsPage}/>

        <Route path="/my-clients" Component={MyClientsPage}/>

        <Route path="/research" Component={ResearchPage}/>
        <Route path="/market-outlooks" Component={MarketOutlookPage}/>
        <Route path="/white-papers" Component={WhitePapersPage}/>

        <Route path="/products/:backstopIdParam" Component={ProductPage}/>
        <Route path="/odd-products/:vehicleIdParam" Component={OddProductPage}/>
        <Route path="/flash-reports/:flashIdParam" Component={FlashReportPage}/>

        <Route path="/products/:productIdParam/meeting-note" Component={MeetingNotePage}/>
        <Route path="/managers/:managerIdParam" Component={ManagerPageConnection}/>

        <Route path="/shared-documents" Component={SharedDocumentsPage}/>
        <Route path="/shared-documents/type/:initialDocumentTypeId" Component={SharedDocumentsPage}/>
        <Route path="/shared-documents/upload-files" Component={UploadPage}/>
        <Route path="/shared-documents/edit/:documentIdParam" Component={AdvancedEditFileComponent}/>
        <Route path="/shared-documents/download/:documentIdParam" Component={DownloadDocumentComponent}/>

        <Route path="/disclaimer" Component={DisclaimerPageConnection}/>
        <Route path="/feedback" Component={FeedbackPage}/>
        <Route path="/feedback-success" Component={FeedbackSuccessPageConnection}/>

        <Route path="/release-notes" Component={ReleaseNotesPage}/>

        <Route path="/odd-iq" Component={OddIqPageContainer}/>
        <Route path="/master-lecture-series" Component={MasterLectureSeriesPage}/>
        <Route path="/client-reporting" Component={PowerBiReportingPage}/>
        <Route path="/admin-upload" Component={AdminUploadPage}/>
        <Route path="/odd-reports" Component={OddReportsPage}/>
        <Route path="/market-outlook-latest/:documentType" Component={LatestMarketOutlookPage}/>
        <Route path="/powerbi-faq-latest" Component={LatestPowerBiFaq}/>
        <Route path="/powerbi-report-admin" Component={PowerBiReportAdminPage}/>
        <Route path="/plan-media-files" Component={PlanSpecificMediaFilesPage}/>
        <Route path="/cpr-shared-media-admin" Component={CprSharedMediaFilesAdminPage}/>
        <Route path="/login/callback" Component={LoginCallback}/>
        <Route path="/portfolio" Component={PortfolioPage}/>
        <Route path="/model-portfolio" Component={ModelPortfolioPage}/>
        <Route path="/*" Component={ErrorComponent}/>
    </Routes>;
};

export const AppRender = (props: ICorsErrorModalOpen) => {
    const { authState } = useOktaAuth();

    return authState?.isAuthenticated
    ? <InitializeUserSession>
                <InitializeIntlComponent>
                    <div>
                        <LayoutHeaderAndBody {...{ setCorsErrorModalOpen: props.setCorsErrorModalOpen }}>
                            <ScrollToTop>
                                <RouteSwitcher/>
                            </ScrollToTop>
                        </LayoutHeaderAndBody>
                        <LayoutFooter/>
                    </div>
                </InitializeIntlComponent>
        </InitializeUserSession>
        : <RouterSwitcherNoAuth/>;
};

export const RootComponent: React.FunctionComponent<IRootComponentProps> = (props) => {
    const oktaAuth = getOktaAuth();
    const [corsErrorModalOpen, setCorsErrorModalOpen] = React.useState(false);
    const [authRequiredModalOpen, setAuthRequiredModalOpen] = React.useState(false);


    const navigate = useNavigate();
    const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri: string) => {
        navigate(originalUri || '/home');
    };

    const customAuthHandler = async () => {
        const previousAuthState = oktaAuth.authStateManager.getPreviousAuthState();
        if (!previousAuthState || !previousAuthState.isAuthenticated) {
            // App initialization stage
            await triggerOktaLogin();
        } else {
            // Ask the user to trigger the login process during token autoRenew process
            setAuthRequiredModalOpen(true);
        }
    };

    return (
            <Security
                oktaAuth={oktaAuth}
                onAuthRequired={customAuthHandler}
                restoreOriginalUri={restoreOriginalUri}
            >
                <CorsErrorModal {...{corsErrorModalOpen, setCorsErrorModalOpen}} />
                <AuthRequiredModal {...
                    {authRequiredModalOpen, setAuthRequiredModalOpen, triggerLogin: triggerOktaLogin}} />
                <Provider store={props.store}>
                    <AppRender {...{setCorsErrorModalOpen}}/>
                </Provider>
            </Security>
    );
};
